import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import AuthForm from '../components/ui/AuthForm';

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://rsp-workers.w315194731.workers.dev';

type Mode = 'signin' | 'signup';

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>('signin');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (email: string) => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Request failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="min-h-[calc(100vh-60px)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors mb-8"
        >
          <ArrowLeft size={14} />
          Back to home
        </Link>

        <div className="bg-bg-surface border border-border-subtle rounded-radius-xl p-8">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-4">
                <Mail size={24} className="text-brand" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary mb-2">Check your inbox</h2>
              <p className="text-sm text-text-secondary leading-relaxed">
                We sent a magic link to <strong>{mode === 'signup' ? 'complete your signup' : 'sign you in'}</strong>.
                Click the link in the message. The link expires in 10 minutes.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm text-brand hover:text-brand-hover transition-colors"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              {/* Mode tabs */}
              <div className="flex gap-1 mb-6 p-1 bg-bg-primary rounded-radius-lg">
                <button
                  onClick={() => setMode('signin')}
                  className={`flex-1 py-2 text-sm font-medium rounded-radius-md transition-colors ${
                    mode === 'signin'
                      ? 'bg-bg-surface text-text-primary shadow-sm'
                      : 'text-text-muted hover:text-text-secondary'
                  }`}
                >
                  Sign in
                </button>
                <button
                  onClick={() => setMode('signup')}
                  className={`flex-1 py-2 text-sm font-medium rounded-radius-md transition-colors ${
                    mode === 'signup'
                      ? 'bg-bg-surface text-text-primary shadow-sm'
                      : 'text-text-muted hover:text-text-secondary'
                  }`}
                >
                  Sign up
                </button>
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-text-primary mb-1">
                  {mode === 'signup' ? 'Create your account' : 'Welcome back'}
                </h2>
                <p className="text-sm text-text-secondary">
                  {mode === 'signup'
                    ? 'Enter your email to get started — no password needed.'
                    : 'Enter your email and we\'ll send you a magic link.'}
                </p>
              </div>

              <AuthForm onSubmit={handleSubmit} />

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border-subtle" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-bg-surface text-text-muted">or</span>
                </div>
              </div>

              <a
                href={`${API_BASE}/api/auth/google`}
                className="flex items-center justify-center gap-3 w-full px-4 py-2.5 border border-border-subtle rounded-radius-lg bg-bg-primary hover:bg-bg-secondary transition-colors text-sm font-medium text-text-primary"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9.003 18c2.43 0 4.467-.805 5.956-2.18l-2.908-2.259c-.805.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9.003 18z" fill="#34A853"/>
                  <path d="M3.967 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.01-2.332z" fill="#FBBC05"/>
                  <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9.003 0A8.997 8.997 0 0 0 .957 4.958L3.967 7.29C4.672 5.163 6.656 3.58 9.003 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </a>

              {error && (
                <p className="mt-4 text-sm text-semantic-error text-center">{error}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}